/**
 * Files from a drop, walking any dropped folders. Folder files carry `path`
 * (`/folder/sub/a.jpg`), the property react-dropzone set, so callers can map
 * folders to collections. Hidden entries inside folders and unreadable files are skipped.
 */
export async function filesFromDrop(transfer) {
    if (!transfer)
        return [];
    // Entries and loose files must be read synchronously, before the drop event ends.
    const items = Array.from(transfer.items ?? []).filter((item) => item.kind === 'file');
    const entries = items.map((item) => item.webkitGetAsEntry?.() ?? null);
    if (!entries.some((entry) => entry?.isDirectory))
        return Array.from(transfer.files ?? []);
    const loose = items.map((item) => item.getAsFile());
    const groups = await Promise.all(entries.map((entry, index) => {
        if (entry?.isDirectory)
            return walk(entry);
        const file = loose[index];
        return file ? [file] : [];
    }));
    return groups.flat();
}
async function walk(entry) {
    if (entry.isFile) {
        if (!entry.file)
            return [];
        // One unreadable file (broken symlink, revoked permission) skips itself, not the whole drop.
        const file = await new Promise((resolve, reject) => entry.file(resolve, reject)).catch(() => null);
        if (!file)
            return [];
        Object.defineProperty(file, 'path', { value: entry.fullPath, configurable: true });
        return [file];
    }
    if (!entry.createReader)
        return [];
    const reader = entry.createReader();
    const children = [];
    // readEntries returns at most ~100 entries per call; read until it comes back empty.
    for (;;) {
        const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject)).catch(() => []);
        if (!batch.length)
            break;
        children.push(...batch);
    }
    // Hidden files and folders inside a drop (.DS_Store, .git) are never media.
    const groups = await Promise.all(children.filter((child) => !child.name.startsWith('.')).map(walk));
    return groups.flat();
}
//# sourceMappingURL=entries.js.map